import React from 'react';
import {useAutoList} from '@netx/core-next/hooks/autoList/useAutoList';
import {
	getLabelValueAutoListOptionLabel,
	isLabelValueAutoListOptionSelected,
} from '@netx/core-next/utils/autoList';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {
	AutoListField,
	type AutoListCommonExtendProps,
} from '@netx/core-components/AutoListField';
import {getAssetAutoList} from '../../api';
import {type IAssetAutoListItem} from '../../schema';

export type AssetAutoListRef = React.ForwardedRef<HTMLDivElement>;

const DEFAULT_NAME = 'asset-auto-list';

export interface AssetAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends AutoListCommonExtendProps<
	IAssetAutoListItem,
	Multiple,
	DisableClearable,
	false
> {}

export const AssetAutoListPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		filterOptions,
		getOptionLabel = getLabelValueAutoListOptionLabel,
		getOptionSelected = isLabelValueAutoListOptionSelected,
		onClose,
		onInputChange,
		onOpen,
		...props
	}: AssetAutoListProps<Multiple, DisableClearable>,
	ref: AssetAutoListRef,
) => {
	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			filterOptions,
			queryFn: getAssetAutoList,
		},
	);

	return (
		<AutoListField<IAssetAutoListItem, Multiple, DisableClearable>
			ref={ref}
			label={i18n.t('i18n.asset')}
			loadingOptions={loadingOptions}
			multiple={false as Multiple}
			name={DEFAULT_NAME}
			placeholder={i18n.t('i18n.pleaseSelect')}
			isOptionEqualToValue={getOptionSelected}
			{...props}
			inputValue={queryStr}
			options={query.data ?? []}
			getOptionLabel={getOptionLabel}
			onClose={splitEventHandler(onClose, () => setIsOpen(false))}
			onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
			onInputChange={splitEventHandler(onInputChange, (event, value, reason) =>
				setQueryStr(value),
			)}
		/>
	);
};

export const AssetAutoList = React.forwardRef(AssetAutoListPrimitive) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: AssetAutoListProps<Multiple, DisableClearable>,
) => JSX.Element;

export default AssetAutoList;
